export interface IntRange {
  start: number
  end: number
}

interface Bounds {
  max?: number
  min: number
}
/**
 * Calculates the range immediately before the given range.
 *
 * @param range - The current range.
 * @param range.start - The start of the current range.
 * @param chunkSize - The size of the chunk to subtract.
 * @returns The previous range before the current range, or null if it goes below 1.
 */
export function before({ start }: IntRange, chunkSize: number): IntRange {
  return {
    start: start - chunkSize,
    end: start - 1,
  }
}

/**
 * Calculates the range immediately after the given range.
 *
 * @param range - The current range.
 * @param range.end - The end of the current range.
 * @param chunkSize - The size of the chunk to add.
 * @returns The next range after the current range.
 */
export function after({ end }: IntRange, chunkSize: number): IntRange {
  return {
    start: end + 1,
    end: end + chunkSize,
  }
}

/**
 * Clamps a range within valid bounds (minimum 1 and optional maximum).
 *
 * @param range - The candidate range to clamp.
 * @param range.start - The start of the candidate range.
 * @param range.end - The end of the candidate range.
 * @param bounds
 * @param bounds.max - The optional maximum page number to clamp to.
 * @param bounds.min - The minimum page number (default is 1).
 * @returns The clamped range, or null if it is out of bounds.
 */
export function clamp({ start, end }: IntRange, { max, min }: Bounds = { min: 1 }): IntRange | null {
  if (max !== undefined) {
    if (start > max) {
      return null
    }
    if (end > max) {
      end = max
    }
  }

  if (start < min) {
    start = min
  }

  if (start > end) {
    return null
  }

  return { start, end }
}
